import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { ArrowRight, ArrowLeft, CheckCircle, Brain, TrendingUp, Target } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useTheme } from '@/contexts/ThemeContext';

const SkillsAssessment = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const assessmentType = location.state?.type || 'pre';
  
  const [questions, setQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const QUESTIONS_PER_PAGE = 5;
  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);

  useEffect(() => {
    fetchQuestions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments/questions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuestions(data);
      }
    } catch (error) {
      toast.error('Failed to load assessment');
    }
    setLoading(false);
  };

  const handleAnswer = (questionId, answer) => {
    setResponses({ ...responses, [questionId]: answer });
  };

  const getCurrentPageQuestions = () => {
    const start = currentPage * QUESTIONS_PER_PAGE;
    const end = start + QUESTIONS_PER_PAGE;
    return questions.slice(start, end);
  };

  const isPageComplete = () => {
    const pageQuestions = getCurrentPageQuestions();
    return pageQuestions.every(q => responses[q.id]);
  };

  const handleNext = () => {
    if (!isPageComplete()) {
      toast.error('Please answer all questions on this page');
      return;
    }
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const responseArray = Object.entries(responses).map(([question_id, answer]) => ({
        question_id,
        answer
      }));

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/assessments/submit?assessment_type=${assessmentType}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ responses: responseArray })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        confetti({
          particleCount: 150,
          spread: 100,
          origin: { y: 0.6 }
        });
        toast.success(`Assessment complete! +${data.points_earned} points`  + String.fromCharCode(127881));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit assessment');
      }
    } catch (error) {
      toast.error('Connection error');
    }
    setSubmitting(false);
  };

  const { colors } = useTheme();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
        <div className="text-2xl font-bold text-orange-500">Loading assessment...</div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F5F3EF' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-2xl"
        >
          <Card className="bg-white rounded-3xl shadow-floating p-8 md:p-12 text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Brain className="w-12 h-12 text-white" />
            </div>
            
            <h2 className="font-heading text-4xl font-bold mb-4">
              {assessmentType === 'pre' ? 'Baseline Set!' : 'Amazing Growth!'}
            </h2>
            
            <p className="text-lg text-muted-foreground mb-8">
              {assessmentType === 'pre' 
                ? "You've completed your starting assessment. We'll track your growth throughout the program!"
                : "Check out how much you've grown since day one!"
              }
            </p>

            <div className="bg-orange-50 rounded-2xl p-6 mb-4">
              <p className="text-xs font-bold text-orange-500 mb-1">Your Score</p>
              <p className="text-5xl font-bold text-orange-600 mb-1">
                {Math.round(result.overall_score)}
              </p>
              <p className="text-gray-400 text-sm">out of 100</p>
            </div>

            {/* AI Feedback */}
            {result.ai_feedback ? (
              <div className="text-left mb-6 space-y-3">
                {result.ai_feedback.strengths?.length > 0 && (
                  <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-100">
                    <p className="text-xs font-bold text-emerald-700 uppercase mb-2">Your Strengths</p>
                    {result.ai_feedback.strengths.map((s, i) => (
                      <p key={i} className="text-sm text-emerald-800 mb-1">+ {s}</p>
                    ))}
                  </div>
                )}
                {result.ai_feedback.growth_areas?.length > 0 && (
                  <div className="p-4 rounded-2xl bg-amber-50 border border-amber-100">
                    <p className="text-xs font-bold text-amber-700 uppercase mb-2">Areas to Grow</p>
                    {result.ai_feedback.growth_areas.map((g, i) => (
                      <p key={i} className="text-sm text-amber-800 mb-1">~ {g}</p>
                    ))}
                  </div>
                )}
                {result.ai_feedback.recommendations?.length > 0 && (
                  <div className="p-4 rounded-2xl bg-blue-50 border border-blue-100">
                    <p className="text-xs font-bold text-blue-700 uppercase mb-2">Recommendations</p>
                    {result.ai_feedback.recommendations.map((r, i) => (
                      <p key={i} className="text-sm text-blue-800 mb-1">→ {r}</p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400 mb-4">AI feedback generating...</p>
            )}

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-accent/10 rounded-2xl p-4">
                <TrendingUp className="w-8 h-8 text-accent mx-auto mb-2" />
                <p className="text-2xl font-heading font-bold text-accent">+{result.points_earned}</p>
                <p className="text-xs text-muted-foreground">Points Earned</p>
              </div>
              <div className="bg-secondary/10 rounded-2xl p-4">
                <Target className="w-8 h-8 text-secondary mx-auto mb-2" />
                <p className="text-2xl font-heading font-bold text-secondary">{questions.length}</p>
                <p className="text-xs text-muted-foreground">Questions Completed</p>
              </div>
            </div>

            <Button
              data-testid="back-to-dashboard-button"
              onClick={() => navigate('/dashboard')}
              className="w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold"
            >
              Back to Dashboard
            </Button>
          </Card>
        </motion.div>
      </div>
    );
  }

  const pageQuestions = getCurrentPageQuestions();
  const progress = ((currentPage + 1) / totalPages) * 100;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200">
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-gray-900">
                {assessmentType === 'pre' ? 'Skills Tracker' : 'Growth Check'}
              </h1>
              <p className="text-xs text-gray-400">
                Page {currentPage + 1} of {totalPages}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-orange-500">{Math.round(progress)}%</p>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="space-y-5"
          >
            {pageQuestions.map((question, idx) => (
              <Card key={question.id} className="rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-orange-500">
                      {currentPage * QUESTIONS_PER_PAGE + idx + 1}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-gray-900 leading-relaxed">{question.question_text}</h3>
                    {question.category && <span className="text-[9px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-bold mt-1 inline-block">{question.category}</span>}
                  </div>
                </div>

                <div className="space-y-2 ml-12">
                  {question.options.map((option) => (
                    <motion.button
                      key={option}
                      data-testid={`question-${question.id}-option`}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleAnswer(question.id, option)}
                      className={`w-full p-3.5 rounded-xl text-left text-sm font-medium transition-all ${
                        responses[question.id] === option
                          ? 'bg-orange-50 ring-2 ring-orange-400 text-orange-800'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {option}
                    </motion.button>
                  ))}
                </div>
              </Card>
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="flex gap-3 pb-8">
          {currentPage > 0 && (
            <Button
              data-testid="back-button"
              onClick={handleBack}
              variant="outline"
              className="flex-1 h-12 rounded-xl font-bold"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          )}
          <Button
            data-testid={currentPage === totalPages - 1 ? "submit-button" : "next-button"}
            onClick={handleNext}
            disabled={!isPageComplete() || submitting}
            className="flex-1 h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold"
          >
            {submitting ? 'Submitting...' : currentPage === totalPages - 1 ? (
              <><CheckCircle className="w-4 h-4 mr-2" /> Submit</>
            ) : (
              <>Next <ArrowRight className="w-4 h-4 ml-2" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SkillsAssessment;